import {
  Answers,
  FileType,
  Task,
  ValidationType,
  WorkModel,
} from 'models/task.model';
import { create } from 'zustand';
import {
  validateAnswer,
  validateComment,
  validateDescription,
  validateErrorMessage,
  validateSuccessMessage,
  validateTitle,
  validateWorkModels,
} from './task-validation-helper';
import {
  DraftPredefinedOptionsValidation,
  DraftSetOptions,
  DraftTaskKeys,
  DraftTaskValidationField,
  TaskActions,
  TaskState,
} from './task.type';
import { AllStatuses } from 'interfaces';

const answersDefaultValue: Answers = { containsOneOf: [], matchesOneOf: [] };

const initialTask: Task = {
  id: '',
  title: '',
  description: '',
  attachments: [],
  workModels: ['WORK_FROM_OFFICE'],
  validator: 'ACCEPT_BUTTON',
  answers: answersDefaultValue,
  errorMessage: '',
  successMessage: '',
  comment: '',
  taskGroupId: null,
  /* TODO: add isDraftSaved flag derived from status (also relevant for recognitions and broadcasts)
  1) possible solutions are listed here: https://github.com/pmndrs/zustand/issues/108
  2) demo solution by zustand contributor: https://stackblitz.com/edit/zustand-computed-fields-and-inferred-types
  */
  status: AllStatuses.NOT_SAVED,
  order: '',
  author: null,
  createdAt: '',
  updatedAt: '',
};

const initialTaskErrors: DraftTaskKeys & DraftPredefinedOptionsValidation = {
  title: '',
  description: '',
  attachments: '',
  workModels: '',
  validator: '',
  answers: '',
  errorMessage: '',
  successMessage: '',
  comment: '',
  taskGroupId: '',
  status: '',
  order: '',
  predefinedOptionCorrectAnswer: '',
  predefinedOptionIncorrectAnswer: '',
};

const initialState: TaskState = {
  tasks: [],
  draft: initialTask,
  errors: initialTaskErrors,
  assignedGroup: initialTask.taskGroupId,
};

const updateTaskList = (state: TaskState) => {
  const { tasks, draft } = state;

  const index = tasks.findIndex((item) => item.id === draft.id);

  if (index === -1) {
    return tasks;
  }

  return tasks.toSpliced(index, 1, draft);
};

const updateTaskField = (
  state: TaskState,
  options: DraftSetOptions & {
    field: keyof TaskState['draft'];
    value: TaskState['draft'][keyof TaskState['draft']];
  },
): TaskState => {
  const { draft, errors } = state;
  const { field, value, markAsDirty } = options;

  const updatedDraft: TaskState['draft'] = {
    ...draft,
    [field]: value,
    ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
  };

  const updatedTasks = updateTaskList({
    ...state,
    draft: updatedDraft,
  });

  return {
    ...state,
    errors: {
      ...errors,
      [field]: '',
    },
    draft: updatedDraft,
    tasks: updatedTasks,
  };
};

export const useTaskStore = create<TaskState & TaskActions>((set) => ({
  ...initialState,
  actions: {
    setDraftTitle: (
      title: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          title,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const titleError = shouldValidate && validateTitle(title, true);

        if (titleError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              title: titleError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'title',
          value: title,
          markAsDirty,
        });
      });
    },
    setDraftDescription: (
      description: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          description,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const descriptionError =
          shouldValidate && validateDescription(description);

        if (descriptionError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              description: descriptionError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'description',
          value: description,
          markAsDirty,
        });
      });
    },
    setDraftPictures: (
      files: FileType[],
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) =>
      set((state) => {
        const { draft } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          attachments: files,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        return {
          ...state,
          draft: updatedDraft,
          tasks: updatedTasks,
        };
      }),
    addDraftPredefinedOptionsValidator: () => {
      set((state) => {
        return {
          ...state,
          draft: {
            ...state.draft,
            answers: {
              ...state.draft.answers,
              predefinedOptions: {
                answers: [],
                title: '',
              },
            },
          },
        };
      });
    },
    removeDraftPredefinedOptionsValidator() {
      set((state) => {
        return {
          ...state,
          draft: {
            ...state.draft,
            answers: {
              ...state.draft.answers,
              predefinedOptions: undefined,
            },
          },
        };
      });
    },
    setDraftWorkModels: (
      workModels: WorkModel[],
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          workModels,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const workModelsError =
          shouldValidate && validateWorkModels(workModels);

        if (workModelsError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              workModels: workModelsError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'workModels',
          value: workModels,
          markAsDirty,
        });
      });
    },
    setDraftValidator: (
      validator: ValidationType,
      { markAsDirty }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;
        const emptyPredefinedOptions: Answers['predefinedOptions'] = {
          answers: [],
        };
        const updatedDraft: TaskState['draft'] = {
          ...draft,
          validator,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
          answers: {
            containsOneOf: state.draft.answers.containsOneOf,
            matchesOneOf: state.draft.answers.matchesOneOf,
            predefinedOptions:
              validator === 'PREDEFINED_OPTIONS'
                ? emptyPredefinedOptions
                : undefined,
          },
        };
        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        return {
          errors: {
            ...errors,
            validator: '',
            answers: '',
          },
          draft: updatedDraft,
          tasks: updatedTasks,
        };
      });
    },
    setDraftQuestionAnswer: (
      answers: {
        title: string;
        isCorrect: boolean;
      }[],
    ) => {
      set((state) => {
        const {
          draft: {
            answers: { predefinedOptions },
          },
        } = state;

        return {
          ...state,
          draft: {
            ...state.draft,
            status: AllStatuses.NOT_SAVED,
            answers: {
              ...state.draft.answers,
              predefinedOptions: {
                ...predefinedOptions,
                answers,
              },
            },
          },
        };
      });
    },
    setDraftAnswer: (
      answers: Answers,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;
        const { validator } = draft;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          answers,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const answersError =
          shouldValidate && validateAnswer(validator, answers);

        if (answersError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              answers: answersError,
            },
            tasks: updatedTasks,
          };
        }

        return {
          errors: {
            ...errors,
            answers: '',
            validator: '',
          },
          draft: updatedDraft,
          tasks: updatedTasks,
        };
      });
    },
    setDraftErrorMessage: (
      errorMessage: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          errorMessage,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const errorMessageError =
          shouldValidate && validateErrorMessage(errorMessage);

        if (errorMessageError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              errorMessage: errorMessageError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'errorMessage',
          value: errorMessage,
          markAsDirty,
        });
      });
    },
    setDraftSuccessMessage: (
      successMessage: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          successMessage,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const successMessageError =
          shouldValidate && validateSuccessMessage(successMessage);

        if (successMessageError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              successMessage: successMessageError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'successMessage',
          value: successMessage,
          markAsDirty,
        });
      });
    },
    setDraftComment: (
      comment: string,
      { markAsDirty, shouldValidate }: DraftSetOptions = {
        markAsDirty: true,
        shouldValidate: true,
      },
    ) => {
      set((state) => {
        const { draft, errors } = state;

        const updatedDraft: TaskState['draft'] = {
          ...draft,
          comment,
          ...(markAsDirty ? { status: AllStatuses.NOT_SAVED } : {}),
        };

        const updatedTasks = updateTaskList({
          ...state,
          draft: updatedDraft,
        });

        const commentError = shouldValidate && validateComment(comment);

        if (commentError) {
          return {
            ...state,
            draft: updatedDraft,
            errors: {
              ...errors,
              comment: commentError,
            },
            tasks: updatedTasks,
          };
        }

        return updateTaskField(state, {
          field: 'comment',
          value: comment,
          markAsDirty,
        });
      });
    },
    setDraftGroupId: (
      { markAsDirty }: DraftSetOptions = { markAsDirty: true },
    ) => {
      set((state) =>
        updateTaskField(state, {
          field: 'taskGroupId',
          value: state.draft.taskGroupId,
          markAsDirty,
        }),
      );
    },
  },
}));

export const setTasks = (tasks: Task[]) => {
  useTaskStore.setState(({ draft }) => {
    let updatedTasks = tasks;

    if (draft.status === AllStatuses.NOT_SAVED) {
      updatedTasks = tasks.map((task) => {
        if (task.id === draft.id) {
          return draft;
        }

        return task;
      });
    }

    return { tasks: updatedTasks };
  });
};

export const setAssignedGroup = (assignedGroup: Task['taskGroupId']) => {
  useTaskStore.setState({ assignedGroup });
};

export const resetDraft = () => {
  useTaskStore.setState(() => ({
    draft: initialState.draft,
    errors: initialState.errors,
  }));
};

export const setDraftTask = ({
  task,
  assignedGroup,
}: {
  task: Task;
  assignedGroup?: TaskState['assignedGroup'];
}) => {
  useTaskStore.setState({ draft: task, errors: initialState.errors });
  setAssignedGroup(assignedGroup || task.taskGroupId);
};

export const setTaskErrors = (
  newErrors: Partial<
    DraftTaskValidationField & DraftPredefinedOptionsValidation
  >,
) => {
  const { errors } = useTaskStore.getState();

  useTaskStore.setState({
    errors: {
      ...errors,
      ...newErrors,
    } as DraftTaskKeys & DraftPredefinedOptionsValidation,
  });
};

export const taskTemplate = initialTask;
